// src/components/ShippingAndDelivery.jsx
import React, { useMemo } from "react";

import NoticeBox from "./common/NoticeBox";

const ShippingAndDelivery = () => {
  const header = useMemo(
    () => ({
      badge: "Fast & Reliable",
      title: "Shipping & Delivery",
      description:
        "We currently deliver within the United States with a flat delivery charge of $6.99. Estimated delivery time may vary based on the delivery location.",
    }),
    []
  );

  const shippingOptions = useMemo(
    () => [
      {
        name: "United States Delivery",
        time: "Delivery time varies by location",
        cost: "$6.99",
        description: "Flat delivery charge for eligible US locations",
      },
    ],
    []
  );

  const deliveryEstimate = useMemo(
    () => ({
      title: "Estimated Delivery Time",
      description:
        "Estimated delivery time may vary depending on your delivery location, carrier availability, weather conditions, holidays, and other factors beyond our control.",
      charge: "$6.99",
    }),
    []
  );

  const shippingInfo = useMemo(
    () => [
      {
        title: "Domestic Shipping",
        content: `We currently ship within the United States only.

Delivery Charge: $6.99
Estimated Delivery Time: Varies based on delivery location

The delivery charge is the same for all eligible locations in the United States.

Orders are processed Monday to Friday, excluding holidays. Orders placed after 2 PM EST may ship the next business day.`,
      },
      {
        title: "Order Processing Time",
        content: `Standard orders are usually processed within 1-2 business days.

Custom or personalized items may take 3-5 business days.

Orders placed before 2 PM EST may ship the same business day.

You will receive a shipping confirmation email with tracking information once your order ships.`,
      },
      {
        title: "Tracking Your Order",
        content: `After your order ships, you will receive a tracking number by email.

You can track your package using the tracking link in the email or through the carrier website.

Tracking updates usually appear within 24 hours of shipment.`,
      },
      {
        title: "Delivery Options",
        content: `Standard delivery may be left at your door, mailbox, or designated delivery area.

Please include apartment, unit, suite, or business details to avoid delivery delays.

Signature confirmation may be required for some high-value orders.`,
      },
      {
        title: "Shipping Restrictions",
        content: `We currently do not offer international shipping.

Some addresses may have limited delivery options depending on carrier availability.

Some items may have shipping restrictions due to size, weight, or product type.`,
      },
      {
        title: "Holidays & Delays",
        content: `We do not ship on federal holidays.

Possible delays may happen during peak holiday seasons, severe weather, or carrier disruptions.

Delivery times are estimates and may vary based on the delivery location, carrier, and other external factors.`,
      },
      {
        title: "Lost or Damaged Packages",
        content: `If your package is not delivered within the estimated time plus 5 business days, please contact us.

For damaged packages, report the issue within 48 hours and include photos.

We will help arrange an investigation, replacement, or return when applicable.`,
      },
      {
        title: "Multiple Items & Split Shipments",
        content: `Orders with multiple items may ship separately from different warehouses.

You may receive separate tracking numbers for each shipment.

Split shipments do not increase your delivery charge unless stated otherwise at checkout.`,
      },
      {
        title: "Address Changes",
        content: `Before shipment, contact us immediately if you need to update your address.

After shipment, address changes may not be possible. You may need to contact the carrier directly.

We are not responsible for packages shipped to incorrect addresses provided at checkout.`,
      },
    ],
    []
  );

  const shippingTips = useMemo(
    () => [
      "Double-check your shipping address before checkout.",
      "Include apartment, unit, or suite number when needed.",
      "Use tracking to monitor your package.",
      "Contact us quickly if your package is lost or damaged.",
    ],
    []
  );

  const notice = useMemo(
    () => ({
      type: "info",
      icon: "ph-info",
      title: "Shipping Notice",
      message:
        "We currently ship within the United States only. A flat delivery charge of $6.99 applies to eligible US locations. Estimated delivery time may vary depending on the delivery location, carrier availability, weather, holidays, and other circumstances beyond our control.",
    }),
    []
  );

  return (
    <section className="shipping-delivery">
      <style>
        {`
          .shipping-delivery {
            padding: 40px 0;
            background: #fafafa;
          }

          .shipping-delivery .shipping-wrapper {
            margin: 0 auto;
          }

          .shipping-delivery .shipping-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .shipping-delivery .shipping-badge {
            display: inline-block;
            font-size: 13px;
            font-weight: 600;
            color: #0d6efd;
            background: #eef5ff;
            padding: 6px 14px;
            border-radius: 50px;
            margin-bottom: 12px;
          }

          .shipping-delivery .shipping-title {
            font-size: 34px;
            line-height: 1.2;
            margin-bottom: 10px;
            color: #222;
          }

          .shipping-delivery .shipping-description {
            max-width: 620px;
            margin: 0 auto;
            font-size: 15px;
            line-height: 1.6;
            color: #666;
          }

          .shipping-delivery .shipping-section {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 22px;
            margin-bottom: 16px;
          }

          .shipping-delivery .shipping-section-title {
            font-size: 20px;
            line-height: 1.3;
            margin-bottom: 14px;
            color: #222;
            font-weight: 700;
          }

          .shipping-delivery .shipping-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 14px;
          }

          .shipping-delivery .shipping-option {
            border: 1px solid #eee;
            border-radius: 10px;
            padding: 16px;
            background: #fafafa;
          }

          .shipping-delivery .shipping-option-title {
            font-size: 16px;
            font-weight: 700;
            color: #222;
            margin-bottom: 6px;
          }

          .shipping-delivery .shipping-option-price {
            font-size: 15px;
            font-weight: 700;
            color: #0d6efd;
            margin-bottom: 4px;
          }

          .shipping-delivery .shipping-option-text {
            font-size: 14px;
            line-height: 1.6;
            color: #666;
            margin-bottom: 0;
          }

          .shipping-delivery .shipping-info-text {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 0;
            white-space: pre-line;
          }

          .shipping-delivery .delivery-charge {
            margin-top: 12px;
            font-size: 14px;
            line-height: 1.7;
            color: #666;
          }

          .shipping-delivery .delivery-charge strong {
            color: #222;
          }

          .shipping-delivery .shipping-list {
            padding-left: 20px;
            margin-bottom: 0;
          }

          .shipping-delivery .shipping-list li {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 8px;
          }

          .shipping-delivery .shipping-list li:last-child {
            margin-bottom: 0;
          }

          .shipping-delivery .notice-small {
            margin-top: 24px;
          }

          @media (max-width: 575px) {
            .shipping-delivery {
              padding: 30px 0;
            }

            .shipping-delivery .shipping-title {
              font-size: 28px;
            }

            .shipping-delivery .shipping-section {
              padding: 18px;
            }

            .shipping-delivery .shipping-section-title {
              font-size: 18px;
            }
          }
        `}
      </style>

      <div className="container">
        <div className="shipping-wrapper">
          <div className="shipping-header">
            <span className="shipping-badge">{header.badge}</span>

            <h1 className="shipping-title">{header.title}</h1>

            <p className="shipping-description">{header.description}</p>
          </div>

          <div className="shipping-section">
            <h3 className="shipping-section-title">Shipping Option</h3>

            <div className="shipping-grid">
              {shippingOptions.map((option, index) => (
                <div className="shipping-option" key={index}>
                  <h4 className="shipping-option-title">{option.name}</h4>
                  <p className="shipping-option-price">{option.cost}</p>
                  <p className="shipping-option-text">{option.time}</p>
                  <p className="shipping-option-text">{option.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="shipping-section">
            <h3 className="shipping-section-title">{deliveryEstimate.title}</h3>

            <p className="shipping-info-text">
              {deliveryEstimate.description}
            </p>

            <p className="delivery-charge">
              Delivery Charge: <strong>{deliveryEstimate.charge}</strong>
            </p>
          </div>

          {shippingInfo.map((section, index) => (
            <div key={index} className="shipping-section">
              <h3 className="shipping-section-title">{section.title}</h3>
              <p className="shipping-info-text">{section.content}</p>
            </div>
          ))}

          <div className="shipping-section">
            <h3 className="shipping-section-title">Shipping Tips</h3>

            <ul className="shipping-list">
              {shippingTips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>

          <NoticeBox
            type={notice.type}
            icon={notice.icon}
            title={notice.title}
            message={notice.message}
            className="notice-small"
          />
        </div>
      </div>
    </section>
  );
};

export default ShippingAndDelivery;